import type { useEffectiveGrants } from '@/lib/me'

export const employeeTabs = [
  'overview',
  'org',
  'employment-history',
  'training',
  'separation',
  'salary-components',
  'documents',
  'assets',
  'violations',
  'expenses',
  'cash-custody',
  'performance',
  'payslips',
  'time-off',
  'attendance',
  'hybrid-work',
] as const

/**
 * Manager-facing groups, each with five sections or fewer. Every legacy key
 * stays a valid deep-link input, and so do the old group keys `job-pay` and
 * `time`, which bookmarks and links may still carry.
 */
export const employeeTabGroups = [
  'overview',
  'job',
  'pay',
  'schedule',
  'leave',
  'documents-assets',
  'performance',
  'money',
  'personal-access',
] as const

/** Group keys the page used before the nine-tab split. */
const formerGroups = ['job-pay', 'time'] as const

export type EmployeeTabGroup = (typeof employeeTabGroups)[number]
export type LegacyEmployeeTab = (typeof employeeTabs)[number]
type FormerGroup = (typeof formerGroups)[number]
export type EmployeeTab = EmployeeTabGroup | LegacyEmployeeTab | FormerGroup

export const employeeTabKeys: readonly EmployeeTab[] = [
  ...employeeTabs,
  ...employeeTabGroups,
  ...formerGroups,
]

const legacyTabGroups: Record<LegacyEmployeeTab | FormerGroup, EmployeeTabGroup> = {
  overview: 'overview',
  org: 'job',
  'employment-history': 'job',
  training: 'job',
  separation: 'job',
  'salary-components': 'pay',
  documents: 'documents-assets',
  assets: 'documents-assets',
  violations: 'performance',
  expenses: 'money',
  'cash-custody': 'money',
  performance: 'performance',
  payslips: 'money',
  'time-off': 'leave',
  attendance: 'schedule',
  'hybrid-work': 'schedule',
  'job-pay': 'job',
  time: 'schedule',
}

export function employeeTabGroup(tab?: EmployeeTab): EmployeeTabGroup {
  if (!tab) return 'overview'
  if (isLegacyEmployeeTab(tab) || isFormerGroup(tab)) return legacyTabGroups[tab]
  return tab
}

export function isLegacyEmployeeTab(tab: EmployeeTab): tab is LegacyEmployeeTab {
  return employeeTabs.includes(tab as LegacyEmployeeTab)
}

function isFormerGroup(tab: EmployeeTab): tab is FormerGroup {
  return formerGroups.includes(tab as FormerGroup)
}

/**
 * Whether the strip shows a group. A group the caller cannot open is ABSENT,
 * never disabled. Overview, Job and Personal & access need no key of their
 * own: the route already requires `employee.view`.
 */
export function employeeTabGroupVisible(
  group: EmployeeTabGroup,
  can: ReturnType<typeof useEffectiveGrants>['can'],
): boolean {
  switch (group) {
    case 'pay':
      return can('payroll_component.view') || can('employee_financial.view')
    case 'leave':
      return can('leave.view')
    case 'documents-assets':
      return can('employee_document.view') || can('asset.view')
    case 'performance':
      return can('performance.view') || can('violation.view')
    case 'money':
      return can('expense.view') || can('payslip.view') || can('cash_custody.view')
    default:
      return true
  }
}

export function employeeTabAllowed(
  tab: EmployeeTab,
  can: ReturnType<typeof useEffectiveGrants>['can'],
): boolean {
  if (!employeeTabGroupVisible(employeeTabGroup(tab), can)) return false
  if (!isLegacyEmployeeTab(tab)) return true
  switch (tab) {
    case 'salary-components':
    case 'payslips':
      return can(tab === 'payslips' ? 'payslip.view' : 'payroll_component.view')
    case 'documents':
      return can('employee_document.view')
    case 'assets':
      return can('asset.view')
    case 'violations':
      return can('violation.view')
    case 'performance':
      return can('performance.view') || can('violation.view')
    case 'expenses':
      return can('expense.view')
    case 'cash-custody':
      return can('cash_custody.view')
    case 'time-off':
      return can('leave.view')
    case 'attendance':
      return can('attendance_identity.edit')
    case 'hybrid-work':
      return can('hybrid_work.edit')
    case 'employment-history':
    case 'training':
    case 'separation':
      return can('employee.view')
    default:
      return true
  }
}
